import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';
import { matchIsValidTel } from 'mui-tel-input';
import z from 'zod';

import { PHONE_VALIDATION_COUNTRIES } from '@/data/constants';

import type { Passenger } from '@/data/models/passenger.model';
import type { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import { TripLegType } from '@/data/models/reservation.model';

import { useDefaultTranslation } from '@/hooks/useCommonTranslation';

import { phoneUtils } from '@/services/phoneUtils';
import reservationUtils from '@/services/reservationUtils';

const isValidUSPhone = (message: string) =>
  z
    .string()
    .refine(
      (v) =>
        matchIsValidTel(v, { onlyCountries: PHONE_VALIDATION_COUNTRIES }) &&
        v !== '+1',
      { message },
    );

const isOptionalValidUSPhone = (message: string) =>
  z
    .string()
    .optional()
    .default('')
    .refine(
      (v) => {
        const value = v ?? '';
        if (!value || value === '+1') return true;
        return matchIsValidTel(value, {
          onlyCountries: PHONE_VALIDATION_COUNTRIES,
        });
      },
      { message },
    );

/** Google Places / passenger autocomplete value (see AddressInput PlaceType) */
const placeTypeSchema = z
  .object({
    description: z.string().optional(),
    mainText: z.string().optional(),
    secondaryText: z.string().optional(),
    mainTextMatchedSubstrings: z
      .array(z.object({ offset: z.number(), length: z.number() }))
      .optional(),
    place_id: z.string().optional(),
    zipCode: z.string().optional(),
    isHeader: z.boolean().optional(),
  })
  .catchall(z.unknown());

const addressTripFieldSchema = (requiredMessage: string) =>
  z.union([z.string(), placeTypeSchema]).superRefine((val, ctx) => {
    if (typeof val === 'string') {
      if (!val.trim()) {
        ctx.addIssue({ code: 'custom', message: requiredMessage });
      }
      return;
    }
    if (val.isHeader === true || val.place_id === 'header') {
      ctx.addIssue({ code: 'custom', message: requiredMessage });
      return;
    }
    const hasAddress =
      Boolean(val.place_id?.trim()) ||
      Boolean(val.mainText?.trim()) ||
      Boolean(val.description?.trim());
    if (!hasAddress) {
      ctx.addIssue({ code: 'custom', message: requiredMessage });
    }
  });

const standingOrderDaySchema = z.object({
  enabled: z.boolean().optional().default(false),
  appointmentTime: z
    .union([
      z.null(),
      z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
    ])
    .optional(),
  pickupTime: z
    .union([
      z.null(),
      z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
    ])
    .optional(),
});

const standingOrderDaysSchema = z.object({
  monday: standingOrderDaySchema,
  tuesday: standingOrderDaySchema,
  wednesday: standingOrderDaySchema,
  thursday: standingOrderDaySchema,
  friday: standingOrderDaySchema,
  saturday: standingOrderDaySchema,
  sunday: standingOrderDaySchema,
});

const tripSegmentSchema = z
  .object({
    tripLegType: z.enum(TripLegType),
    reasonForTrip: z.string().min(1, 'Reason for trip is required'),
    appointmentDate: z.preprocess((val) => {
      if (val === null || val === undefined) return '';
      if (val instanceof DateTime && val.isValid) return val.toISODate();
      return val;
    }, z.string().optional().default('')),
    isWillCall: z.boolean(),
    appointmentDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupDate: z.preprocess((val) => {
      if (val === null || val === undefined) return '';
      if (val instanceof DateTime && val.isValid) return val.toISODate();
      return val;
    }, z.string().optional().default('')),
    pickupDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupTimeZone: z.string().default(''),
    dropoffTimeZone: z.string().default(''),
    pickupAddress: addressTripFieldSchema('Pickup address is required'),
    dropoffAddress: addressTripFieldSchema('Drop-off address is required'),
    pickupAddressStruct: z.string().optional(),
    dropoffAddressStruct: z.string().optional(),
    pickupPhone: isValidUSPhone('Pickup phone is required'),
    pickupPhoneExtension: z.string().optional().default(''),
    dropoffPhone: isOptionalValidUSPhone('Drop-off phone is invalid'),
    dropoffPhoneExtension: z.string().optional().default(''),
    pickupNotes: z.string().optional().default(''),
    dropoffNotes: z.string().optional().default(''),
    roundTripWillCall: z.boolean(),
    roundTripReturnDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrder: z.boolean().optional().default(false),
    standingOrderStartDate: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrderEndDate: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    standingOrderDays: standingOrderDaysSchema.optional(),
    passengerMobility: z.string().min(1, 'Passenger mobility is required'),
    passengerAssistance: z.string().optional(),
    AttributeLevel1: z.string().min(1, 'Attribute level 1 is required'),
    AttributeLevel2: z.string().optional().default(''),
    AttributeLevel3: z.string().optional(),
    AttributeLevel4: z.string().optional(),
    AttributeLevel5: z.string().optional(),
    AttributeLevel6: z.string().optional(),
    MobilityAttribute: z.string().min(1, 'Mobility attribute is required'),
    height: z.string().optional().default(''),
    weight: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.isWillCall) {
      if (!data.appointmentDate || data.appointmentDate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Appointment date is required for will-call trips',
          path: ['appointmentDate'],
        });
      }
      if (!data.pickupDate || data.pickupDate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Pickup date is required for will-call trips',
          path: ['pickupDate'],
        });
      }
      return;
    }

    const appt = data.appointmentDateTime;
    if (
      appt === null ||
      appt === undefined ||
      !(appt instanceof DateTime && appt.isValid)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Appointment date and time is required',
        path: ['appointmentDateTime'],
      });
    }
    const pickup = data.pickupDateTime;
    if (
      pickup === null ||
      pickup === undefined ||
      !(pickup instanceof DateTime && pickup.isValid)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pickup date and time is required',
        path: ['pickupDateTime'],
      });
    }

    if (!data.roundTripWillCall) {
      const returnTime = data.roundTripReturnDateTime;
      if (
        returnTime === null ||
        returnTime === undefined ||
        !(returnTime instanceof DateTime && returnTime.isValid)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Return trip date and time is required',
          path: ['roundTripReturnDateTime'],
        });
      } else if (
        appt instanceof DateTime &&
        appt.isValid &&
        returnTime < appt
      ) {
        ctx.addIssue({
          code: 'custom',
          message: useDefaultTranslation(
            'reservation.returnDateBeforePickupError',
          ),
          path: ['roundTripReturnDateTime'],
        });
      }
    }
  });

const isAppointmentDateRequired = (isWillCall: boolean) => isWillCall;

const isAppointmentDateTimeRequired = (isWillCall: boolean) => !isWillCall;

const isPickupDateTimeRequired = (isWillCall: boolean) => !isWillCall;

type TripSegmentValues = z.infer<typeof tripSegmentSchema>;

const DEFAULT_TRIP_TYPE = TripLegType.RoundTrip;

const emptyTripSegment: z.input<typeof tripSegmentSchema> = {
  tripLegType: DEFAULT_TRIP_TYPE,
  reasonForTrip: '',
  appointmentDate: null,
  isWillCall: false,
  appointmentDateTime: null,
  pickupDate: null,
  pickupDateTime: null,
  pickupAddress: '',
  dropoffAddress: '',
  pickupPhone: '',
  pickupPhoneExtension: '',
  dropoffPhone: '',
  dropoffPhoneExtension: '',
  pickupNotes: '',
  dropoffNotes: '',
  roundTripWillCall: true,
  roundTripReturnDateTime: null,
  standingOrder: false,
  standingOrderStartDate: null,
  standingOrderEndDate: null,
  standingOrderDays: {
    monday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    tuesday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    wednesday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    thursday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    friday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    saturday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
    sunday: {
      enabled: false,
      appointmentTime: null,
      pickupTime: null,
    },
  },
  passengerMobility: '',
  passengerAssistance: '',
  AttributeLevel1: '',
  AttributeLevel2: '',
  AttributeLevel3: '',
  AttributeLevel4: '',
  AttributeLevel5: '',
  AttributeLevel6: '',
  MobilityAttribute: '',
  height: '',
  weight: '',
};

/**
 * When the reverse map includes GURNEY / BARIATRIC_GURNEY UUIDs, member weight is
 * required for trips whose AttributeLevel2 matches. Otherwise (e.g. machine-only
 * parse without the map), non-empty height indicates the gurney measurements UI.
 */
function tripRequiresMemberWeight(
  trip: { AttributeLevel2: string; height: string },
  reverseTransportationAttributeMap: Record<string, string> | null | undefined,
): boolean {
  const gurneyUuid = reverseTransportationAttributeMap?.['GURNEY'];
  const bariatricGurneyUuid =
    reverseTransportationAttributeMap?.['BARIATRIC_GURNEY'];
  const hasGurneyCodesInMap =
    gurneyUuid !== undefined || bariatricGurneyUuid !== undefined;

  if (hasGurneyCodesInMap) {
    return (
      (gurneyUuid !== undefined && trip.AttributeLevel2 === gurneyUuid) ||
      (bariatricGurneyUuid !== undefined &&
        trip.AttributeLevel2 === bariatricGurneyUuid)
    );
  }
  return Boolean(trip.height?.trim());
}

export function getDefaultPickupPhoneAndExtensionFromPassenger(
  passenger: Passenger | null | undefined,
): { pickupPhone: string; pickupPhoneExtension: string } {
  if (!passenger) {
    return { pickupPhone: '', pickupPhoneExtension: '' };
  }
  const pickupPhone = reservationUtils.getPassengerPhone(passenger);
  return {
    pickupPhone: pickupPhone?.phoneNumber ? pickupPhone.phoneNumber : '',
    pickupPhoneExtension: pickupPhone?.phoneExtension || '',
  };
}

const MEMBER_FORM_PHONE_FIELDS = [
  'MemberHomePhone',
  'MemberMobilePhone',
  'AlternateContactPhone',
] as const;

type CreateReservationFormSchemaOptions = {
  /** When false, member phone fields accept any string (trip-details step only). */
  validateMemberPhones?: boolean;
};

function createReservationFormSchema(
  reverseTransportationAttributeMap?: Record<string, string> | null,
  t?: TFunction<'common'>,
  isNonAmbulatoryByIndex?: boolean[] | null,
  options?: CreateReservationFormSchemaOptions,
) {
  const translate = t ?? useDefaultTranslation;
  const validateMemberPhones = options?.validateMemberPhones !== false;
  const optionalPhoneFieldValidation = validateMemberPhones
    ? isOptionalValidUSPhone(translate('common.invalidPhone'))
    : z.string().optional().default('');

  return z
    .object({
      tripType: z.enum(TripLegType),
      genericErrors: z.string().optional().default(''),
      MemberHomePhone: optionalPhoneFieldValidation,
      MemberMobilePhone: optionalPhoneFieldValidation,
      AllowText: z.boolean().optional().default(false),
      ShowOtherInputs: z.boolean().optional().default(false),
      AlternateContact: z.boolean().optional().default(false),
      AlternateContactType: z.string().optional().default(''),
      AlternateContactFirstName: z.string().optional().default(''),
      AlternateContactLastName: z.string().optional().default(''),
      AlternateContactPhone: optionalPhoneFieldValidation,
      AlternateContactAllowSMS: z.boolean().optional().default(false),
      AdditionalPassengers: z.boolean().optional().default(false),
      AdditionalPassengerCount: z.number().nullable().optional().default(null),
      AdditionalAttributes: z.boolean().optional().default(false),
      AllowOverride: z.boolean().optional().default(false),
      OverrideFirstName: z.string().optional().default(''),
      OverrideLastName: z.string().optional().default(''),
      OverrideReason: z.string().optional().default(''),
      OverrideServicePlan: z.string().optional().default(''),
      RequestedProvider: z.boolean().optional().default(false),
      RequestedTransportationProviderID: z
        .number()
        .nullable()
        .optional()
        .default(null),
      PassengerNoteTypes: z.array(z.string()).optional().default([]),
      DriverNoteTypes: z.array(z.string()).optional().default([]),
      trips: z.array(tripSegmentSchema).min(1, 'At least one trip is required'),
    })
    .superRefine((data, ctx) => {
      if (data.tripType !== TripLegType.MultiLeg && data.trips.length !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'One-way mode expects exactly one trip segment',
          path: ['trips'],
        });
      }
    })
    .superRefine((data, ctx) => {
      data.trips.forEach((trip, idx) => {
        if (trip.tripLegType !== data.tripType) {
          ctx.addIssue({
            code: 'custom',
            message: 'Trip leg type must match the selected trip type',
            path: ['trips', idx, 'tripLegType'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      data.trips.forEach((trip, tripIndex) => {
        if (
          !tripRequiresMemberWeight(trip, reverseTransportationAttributeMap)
        ) {
          return;
        }
        const w = String(trip.weight ?? '').trim();
        if (!w) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        const num = parseInt(w, 10);
        if (Number.isNaN(num) || num < 1) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.memberWeightPositive'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        if (num > 999) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.weightValidation'),
            path: ['trips', tripIndex, 'weight'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (isNonAmbulatoryByIndex == null) return;
      data.trips.forEach((trip, tripIndex) => {
        if (isNonAmbulatoryByIndex[tripIndex] !== true) return;
        if (!String(trip.AttributeLevel2 ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'AttributeLevel2'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (!data.AlternateContact) return;

      const requiredMessage = translate('common.requiredField');
      const invalidPhoneMessage = translate('common.invalidPhone');

      if (!String(data.AlternateContactType ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactType'],
        });
      }
      if (!String(data.AlternateContactFirstName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactFirstName'],
        });
      }
      if (!String(data.AlternateContactLastName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactLastName'],
        });
      }

      const phone = String(data.AlternateContactPhone ?? '').trim();
      if (!phone || phone === '+1') {
        ctx.addIssue({
          code: 'custom',
          message: requiredMessage,
          path: ['AlternateContactPhone'],
        });
      } else if (
        !matchIsValidTel(phone, { onlyCountries: PHONE_VALIDATION_COUNTRIES })
      ) {
        ctx.addIssue({
          code: 'custom',
          message: invalidPhoneMessage,
          path: ['AlternateContactPhone'],
        });
      }
    });
}

const reservationFormSchema = createReservationFormSchema(null);

const tripLegReservationFormSchema = createReservationFormSchema(
  null,
  undefined,
  undefined,
  { validateMemberPhones: false },
);

function createTripLegReservationFormSchema(
  reverseTransportationAttributeMap?: Record<string, string> | null,
  t?: TFunction<'common'>,
  isNonAmbulatoryByIndex?: boolean[] | null,
) {
  return createReservationFormSchema(
    reverseTransportationAttributeMap,
    t,
    isNonAmbulatoryByIndex,
    { validateMemberPhones: false },
  );
}

type CreatedReservationFormSchema = ReturnType<
  typeof createReservationFormSchema
>;

type ReservationFormValues = z.infer<typeof reservationFormSchema>;
type ReservationFormInput = z.input<typeof reservationFormSchema>;

const emptyReservationFormValues: ReservationFormInput = {
  tripType: DEFAULT_TRIP_TYPE,
  genericErrors: '',
  MemberHomePhone: '',
  MemberMobilePhone: '',
  AllowText: false,
  ShowOtherInputs: false,
  AlternateContact: false,
  AlternateContactType: '',
  AlternateContactFirstName: '',
  AlternateContactLastName: '',
  AlternateContactPhone: '',
  AlternateContactAllowSMS: false,
  AdditionalPassengers: false,
  AdditionalPassengerCount: null,
  AdditionalAttributes: false,
  AllowOverride: false,
  OverrideFirstName: '',
  OverrideLastName: '',
  OverrideReason: '',
  OverrideServicePlan: '',
  RequestedProvider: false,
  RequestedTransportationProviderID: null,
  PassengerNoteTypes: [],
  DriverNoteTypes: [],
  trips: [emptyTripSegment],
};

function buildEmptyReservationFormValues(
  passenger?: Passenger | null,
  transportationAttributes?: TransportationAttribute[] | null,
): ReservationFormInput {
  const { pickupPhone, pickupPhoneExtension } =
    getDefaultPickupPhoneAndExtensionFromPassenger(passenger);
  const primaryAttrs = reservationUtils.getPrimaryAttributeDefaults(
    undefined,
    transportationAttributes ?? undefined,
  );
  return {
    ...emptyReservationFormValues,
    MemberHomePhone:
      phoneUtils.getPassengerHomePhone(passenger?.passengerPhones ?? [])
        ?.phoneNumber ?? '',
    MemberMobilePhone:
      phoneUtils.getPassengerMobilePhone(passenger?.passengerPhones ?? [])
        ?.phoneNumber ?? '',
    AllowText:
      phoneUtils.getPassengerMobilePhone(passenger?.passengerPhones ?? [])
        ?.hasSMSConsent ?? false,
    trips: [
      {
        ...emptyTripSegment,
        pickupPhone,
        pickupPhoneExtension,
        ...primaryAttrs,
      },
    ],
  };
}

export {
  type CreatedReservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
  type TripSegmentValues,
  DEFAULT_TRIP_TYPE,
  MEMBER_FORM_PHONE_FIELDS,
  createReservationFormSchema,
  createTripLegReservationFormSchema,
  reservationFormSchema,
  tripLegReservationFormSchema,
  emptyReservationFormValues,
  buildEmptyReservationFormValues,
  emptyTripSegment,
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
};
